import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { ShieldIcon, ZapIcon, HeartPulseIcon, BrainIcon, CircleIcon, FlaskConicalIcon, ShieldPlusIcon, BatteryChargingIcon, ArrowRightIcon, Lightbulb } from 'lucide-react';
const LionsMane = () => {
  const [activeSection, setActiveSection] = useState('neurotrophics');
  return <div className="w-full bg-white" data-id="element-956">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-24 pb-16 bg-gradient-to-b from-yellow-50 to-yellow-100" data-id="element-957">
        <div className="max-w-4xl mx-auto" data-id="element-958">
          <div className="w-20 h-1 bg-yellow-500 mx-auto mb-8 rounded-full" data-id="element-959"></div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4" data-id="element-960">
            Intelligence — Lion's Mane
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-961">
            Mental clarity with a clean, crisp undertone.
          </p>
        </div>
      </section>

      {/* Benefits Categories Section with Side Navigation */}
      <section className="py-16 px-4 bg-white" data-id="element-962">
        <div className="container mx-auto" data-id="element-963">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-964">
            Lion's Mane Spora: 9 Benefit Categories
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center mb-6" data-id="element-965">
            Our proprietary extraction technology unlocks the full spectrum of
            Lion's Mane bioactives, delivering nine distinct benefit categories
            in a single cup.
          </p>
          {/* Spec Summary Bar */}
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-xl p-4 mb-16 text-center" data-id="element-966">
            <p className="text-sm font-medium tracking-wide uppercase" data-id="element-967">
              Lion's Mane Spora | Neurotrophics + Immunonutrients + Antioxidants
            </p>
          </div>

          {/* Desktop: Sticky side navigation */}
          <div className="hidden md:flex" data-id="element-968">
            <div className="w-64 sticky top-32 self-start pr-8" data-id="element-969">
              <nav className="flex flex-col space-y-4" data-id="element-970">
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'neurotrophics' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('neurotrophics')} data-id="element-971">
                  <div className="flex items-center gap-3" data-id="element-972">
                    <BrainIcon className="w-5 h-5" data-id="element-973" />
                    <span data-id="element-974">Neurotrophics</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'immunonutrients' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('immunonutrients')} data-id="element-975">
                  <div className="flex items-center gap-3" data-id="element-976">
                    <ShieldPlusIcon className="w-5 h-5" data-id="element-977" />
                    <span data-id="element-978">Immunonutrients</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'antioxidants' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('antioxidants')} data-id="element-979">
                  <div className="flex items-center gap-3" data-id="element-980">
                    <ShieldIcon className="w-5 h-5" data-id="element-981" />
                    <span data-id="element-982">Antioxidants</span>
                  </div>
                </button>
              </nav>
            </div>
            <div className="flex-1 max-w-3xl" data-id="element-983">
              {/* Neurotrophics Section */}
              <section id="neurotrophics" className={`pb-24 ${activeSection === 'neurotrophics' ? 'block' : 'hidden'}`} data-id="element-984">
                <h2 className="text-3xl font-bold mb-8" data-id="element-985">
                  The Neurotrophic Power of Lion's Mane
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-986">
                  Lion's Mane stimulates nerve growth factor production,
                  supports brain cell energy, and improves blood flow to brain
                  tissue for enhanced cognitive function.
                </p>
                {/* 3-column layout for desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-987">
                  {/* Neuroprotective Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-988">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-989">
                      <BrainIcon className="w-6 h-6" data-id="element-990" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-991">
                      Neuroprotective Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-992">
                      Enhanced cognition
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-993">
                      <span className="font-medium" data-id="element-994">Experience:</span> Enhanced
                      memory, learning, and neuroplasticity.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-995">
                      <span className="font-medium" data-id="element-996">Why it works:</span>{' '}
                      Hericenones and erinacines stimulate nerve growth factor
                      (NGF) production.
                    </p>
                    <div className="mt-auto" data-id="element-997">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-998">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-999">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1000">
                          Hericenones
                        </span>
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1001">
                          Erinacines
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Bioenergetics */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1002">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1003">
                      <ZapIcon className="w-6 h-6" data-id="element-1004" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1005">Bioenergetics</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1006">
                      Mental stamina
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1007">
                      <span className="font-medium" data-id="element-1008">Experience:</span> Sustained
                      mental stamina and focus throughout the day.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1009">
                      <span className="font-medium" data-id="element-1010">Why it works:</span>{' '}
                      Polysaccharides support mitochondrial energy production in
                      brain cells.
                    </p>
                    <div className="mt-auto" data-id="element-1011">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1012">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1013">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1014">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Circulatory Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1015">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1016">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-1017" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1018">
                      Circulatory Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1019">
                      Brain blood flow
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1020">
                      <span className="font-medium" data-id="element-1021">Experience:</span> Improved
                      alertness and focus.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1022">
                      <span className="font-medium" data-id="element-1023">Why it works:</span>{' '}
                      Polysaccharides increase blood flow to brain tissue.
                    </p>
                    <div className="mt-auto" data-id="element-1024">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1025">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1026">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1027">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Immunonutrients Section */}
              <section id="immunonutrients" className={`pb-24 ${activeSection === 'immunonutrients' ? 'block' : 'hidden'}`} data-id="element-1028">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1029">
                  The Resilience Matrix
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1030">
                  Lion's Mane contains unique compounds that strengthen
                  immunity, balance stress response, and enhance repair and
                  neuro-regeneration.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1031">
                  {/* Immunonutrients */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1032">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1033">
                      <ShieldPlusIcon className="w-6 h-6" data-id="element-1034" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1035">Immunonutrients</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1036">
                      Foundational resilience
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1037">
                      <span className="font-medium" data-id="element-1038">Experience:</span>{' '}
                      Foundational resilience for long-term focus.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1039">
                      <span className="font-medium" data-id="element-1040">Why it works:</span>{' '}
                      β-glucans and hericenones strengthen immunity and reduce
                      stress load.
                    </p>
                    <div className="mt-auto" data-id="element-1041">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1042">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1043">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1044">
                          β-glucans
                        </span>
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1045">
                          Hericenones
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Adaptogens */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1046">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1047">
                      <CircleIcon className="w-6 h-6" data-id="element-1048" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1049">Adaptogens</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1050">
                      Mental clarity under pressure
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1051">
                      <span className="font-medium" data-id="element-1052">Experience:</span> Stay
                      sharp under pressure.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1053">
                      <span className="font-medium" data-id="element-1054">Why it works:</span>{' '}
                      Hericenones balance stress hormone signaling.
                    </p>
                    <div className="mt-auto" data-id="element-1055">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1056">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1057">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1058">
                          Hericenones
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Endocrine Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1059">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1060">
                      <FlaskConicalIcon className="w-6 h-6" data-id="element-1061" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1062">
                      Endocrine Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1063">
                      Neural regeneration
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1064">
                      <span className="font-medium" data-id="element-1065">Experience:</span>{' '}
                      Brain-body integration and long-term clarity.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1066">
                      <span className="font-medium" data-id="element-1067">Why it works:</span> NGF
                      inducers enhance repair and neuro-regeneration.
                    </p>
                    <div className="mt-auto" data-id="element-1068">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1069">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1070">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1071">
                          NGF inducers
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Antioxidants Section */}
              <section id="antioxidants" className={`pb-24 ${activeSection === 'antioxidants' ? 'block' : 'hidden'}`} data-id="element-1072">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1073">
                  The Protection Network
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1074">
                  Lion's Mane contains powerful compounds that reduce oxidative
                  stress in neurons, lower neuroinflammation, and support oxygen
                  delivery to the brain.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1075">
                  {/* Antioxidants */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1076">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1077">
                      <ShieldIcon className="w-6 h-6" data-id="element-1078" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1079">Antioxidants</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1080">
                      Brain cell protection
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1081">
                      <span className="font-medium" data-id="element-1082">Experience:</span>{' '}
                      Protective support for brain cells.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1083">
                      <span className="font-medium" data-id="element-1084">Why it works:</span>{' '}
                      Phenolic compounds and erinacines reduce oxidative stress
                      in neurons.
                    </p>
                    <div className="mt-auto" data-id="element-1085">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1086">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1087">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1088">
                          Phenolic compounds
                        </span>
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1089">
                          Erinacines
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Anti-Inflammatory */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1090">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1091">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-1092" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1093">
                      Anti-Inflammatory
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1094">
                      Mental clarity
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1095">
                      <span className="font-medium" data-id="element-1096">Experience:</span> Clearer
                      cognition and sharper memory.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1097">
                      <span className="font-medium" data-id="element-1098">Why it works:</span>{' '}
                      Erinacines lower neuroinflammation.
                    </p>
                    <div className="mt-auto" data-id="element-1099">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1100">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1101">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1102">
                          Erinacines
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Respiratory Health */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1103">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1104">
                      <div className="w-6 h-6" data-id="element-1105" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1106">
                      Respiratory Health
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1107">
                      Brain oxygenation
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1108">
                      <span className="font-medium" data-id="element-1109">Experience:</span> Clarity
                      maintained through steady oxygenation.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1110">
                      <span className="font-medium" data-id="element-1111">Why it works:</span>{' '}
                      Polysaccharides support oxygen delivery to the brain.
                    </p>
                    <div className="mt-auto" data-id="element-1112">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1113">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1114">
                        <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium" data-id="element-1115">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Mobile: Collapsible sections */}
          <div className="md:hidden space-y-8" data-id="element-1116">
            {/* Spec Summary Bar - Mobile */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8 text-center" data-id="element-1117">
              <p className="text-xs font-medium tracking-wide uppercase" data-id="element-1118">
                Lion's Mane Spora | Neurotrophics + Immunonutrients +
                Antioxidants
              </p>
            </div>
            <div data-id="element-1119">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'neurotrophics' ? '' : 'neurotrophics')} data-id="element-1120">
                <div className="flex items-center gap-3" data-id="element-1121">
                  <BrainIcon className="w-5 h-5" data-id="element-1122" />
                  <h2 className="text-xl font-bold" data-id="element-1123">Neurotrophic Power</h2>
                </div>
                <span data-id="element-1124">{activeSection === 'neurotrophics' ? '−' : '+'}</span>
              </button>
              {activeSection === 'neurotrophics' && <div className="p-4 space-y-8" data-id="element-1125">
                  {/* Mobile content for Neurotrophics section */}
                </div>}
            </div>
            <div data-id="element-1126">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'immunonutrients' ? '' : 'immunonutrients')} data-id="element-1127">
                <div className="flex items-center gap-3" data-id="element-1128">
                  <ShieldPlusIcon className="w-5 h-5" data-id="element-1129" />
                  <h2 className="text-xl font-bold" data-id="element-1130">The Resilience Matrix</h2>
                </div>
                <span data-id="element-1131">{activeSection === 'immunonutrients' ? '−' : '+'}</span>
              </button>
              {activeSection === 'immunonutrients' && <div className="p-4 space-y-8" data-id="element-1132">
                  {/* Mobile content for Immunonutrients section */}
                </div>}
            </div>
            <div data-id="element-1133">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'antioxidants' ? '' : 'antioxidants')} data-id="element-1134">
                <div className="flex items-center gap-3" data-id="element-1135">
                  <ShieldIcon className="w-5 h-5" data-id="element-1136" />
                  <h2 className="text-xl font-bold" data-id="element-1137">The Protection Network</h2>
                </div>
                <span data-id="element-1138">{activeSection === 'antioxidants' ? '−' : '+'}</span>
              </button>
              {activeSection === 'antioxidants' && <div className="p-4 space-y-8" data-id="element-1139">
                  {/* Mobile content for Antioxidants section */}
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 px-4 bg-black text-white text-center" data-id="element-1140">
        <div className="max-w-4xl mx-auto" data-id="element-1141">
          <h2 className="text-3xl font-bold mb-6" data-id="element-1142">
            Experience Lion's Mane Spora
          </h2>
          <p className="text-xl max-w-2xl mx-auto mb-8" data-id="element-1143">
            Mental clarity with a clean, crisp undertone that enhances cognitive
            function and focus.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1144">
            <Link to="/#products" className="bg-yellow-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-yellow-600 transition-colors" data-id="element-1145">
              Get Spora
            </Link>
            <Link to="/how-it-works" className="bg-transparent text-white border border-white px-8 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-colors" data-id="element-1146">
              Learn The Ritual
            </Link>
            <Link to="/" className="bg-white text-black px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center" data-id="element-1147">
              <span data-id="element-1148">Explore All Varieties</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1149" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default LionsMane;